import React, { useMemo, useState } from "react";
import FlightEngineShell from "../layout/FlightEngineShell";
import FlightSearchScreen from "../search/FlightSearchScreen";
import FlightResultsScreen from "../results/FlightResultsScreen";
import FlightDetailsScreen from "../details/FlightDetailsScreen";
import PassengersScreen from "../passengers/PassengersScreen";
import ExtrasScreen from "../extras/ExtrasScreen";
import ReviewScreen from "../review/ReviewScreen";
import PaymentScreen from "../payment/PaymentScreen";
import ConfirmationScreen from "../confirmation/ConfirmationScreen";
import {
  DETAILS_PREVIEW_STATES,
  FE2_PREVIEW_STEPS,
  MOCK_OFFERS,
  MOCK_REPRICE,
  MOCK_SEARCH,
  getMockOfferById,
} from "../mock/flightEngineMockData";
import {
  EXTRAS_PREVIEW_STATES,
  getMockExtrasCatalog,
} from "../extras/extrasMockData";
import { PAYMENT_PREVIEW_STATES } from "../payment/paymentMockData";
import { CONFIRMATION_PREVIEW_STATES } from "../confirmation/confirmationMockData";
import "./FlightEngineV2PreviewPage.css";

const TITLES = {
  search: "חיפוש טיסות",
  results: "תוצאות",
  details: "פרטי טיסה",
  passengers: "פרטי נוסעים",
  extras: "תוספות וכבודה",
  review: "סיכום והזמנה",
  payment: "תשלום",
  confirmation: "אישור הזמנה",
};

const IMPLEMENTED_STEPS = new Set([
  "search",
  "results",
  "details",
  "passengers",
  "extras",
  "review",
  "payment",
  "confirmation",
]);

export default function FlightEngineV2PreviewPage() {
  const [step, setStep] = useState("search");
  const [search, setSearch] = useState(MOCK_SEARCH);
  const [selectedOfferId, setSelectedOfferId] = useState(MOCK_OFFERS[0].id);
  const [detailsStateId, setDetailsStateId] = useState(DETAILS_PREVIEW_STATES[0].id);
  const [showReprice, setShowReprice] = useState(false);
  const [extrasStateId, setExtrasStateId] = useState(EXTRAS_PREVIEW_STATES[2].id);
  const [paymentStateId, setPaymentStateId] = useState(PAYMENT_PREVIEW_STATES[0].id);
  const [confirmationStateId, setConfirmationStateId] = useState(
    CONFIRMATION_PREVIEW_STATES[0].id
  );

  const detailsState =
    DETAILS_PREVIEW_STATES.find((s) => s.id === detailsStateId) ||
    DETAILS_PREVIEW_STATES[0];
  const detailsOfferId =
    step === "details" ? detailsState.offerId : selectedOfferId;
  const selectedOffer = getMockOfferById(
    step === "details" ? detailsOfferId : selectedOfferId
  );

  const extrasCatalog = useMemo(
    () => getMockExtrasCatalog(extrasStateId),
    [extrasStateId]
  );

  const showProgress = ![
    "search",
    "results",
    "details",
    "passengers",
    "extras",
    "review",
    "payment",
    "confirmation",
  ].includes(step);
  const bare =
    step === "results" ||
    step === "details" ||
    step === "passengers" ||
    step === "extras" ||
    step === "review" ||
    step === "payment" ||
    step === "confirmation";

  const previewNav = (
    <nav className="fe2-preview-nav" aria-label="ניווט Design Preview">
      <strong className="fe2-preview-nav__label">Flight Engine V2 · Preview</strong>
      <div className="fe2-preview-nav__steps">
        {FE2_PREVIEW_STEPS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={
              step === s.id
                ? "fe2-preview-nav__btn fe2-preview-nav__btn--active"
                : "fe2-preview-nav__btn"
            }
            onClick={() => {
              if (
                s.id === "passengers" ||
                s.id === "extras" ||
                s.id === "review" ||
                s.id === "payment" ||
                s.id === "confirmation"
              ) {
                setSelectedOfferId(
                  step === "details" ? detailsOfferId : selectedOfferId
                );
              }
              setStep(s.id);
              if (s.id === "details") {
                setShowReprice(Boolean(detailsState.reprice));
              }
            }}
          >
            {s.label}
            {!IMPLEMENTED_STEPS.has(s.id) ? (
              <span className="fe2-preview-nav__stub">stub</span>
            ) : null}
          </button>
        ))}
      </div>
    </nav>
  );

  const detailsDevControls =
    step === "details" ? (
      <div className="fe2-preview-dev" aria-label="Details mock states">
        <span className="fe2-preview-dev__label">Details states</span>
        {DETAILS_PREVIEW_STATES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={
              detailsStateId === s.id
                ? "fe2-preview-dev__btn fe2-preview-dev__btn--active"
                : "fe2-preview-dev__btn"
            }
            onClick={() => {
              setDetailsStateId(s.id);
              setSelectedOfferId(s.offerId);
              setShowReprice(Boolean(s.reprice));
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
    ) : null;

  const extrasDevControls =
    step === "extras" ? (
      <div className="fe2-preview-dev" aria-label="Extras mock states">
        <span className="fe2-preview-dev__label">Extras states</span>
        {EXTRAS_PREVIEW_STATES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={
              extrasStateId === s.id
                ? "fe2-preview-dev__btn fe2-preview-dev__btn--active"
                : "fe2-preview-dev__btn"
            }
            onClick={() => setExtrasStateId(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
    ) : null;

  const paymentDevControls =
    step === "payment" ? (
      <div className="fe2-preview-dev" aria-label="Payment mock states">
        <span className="fe2-preview-dev__label">Payment states</span>
        {PAYMENT_PREVIEW_STATES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={
              paymentStateId === s.id
                ? "fe2-preview-dev__btn fe2-preview-dev__btn--active"
                : "fe2-preview-dev__btn"
            }
            onClick={() => setPaymentStateId(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
    ) : null;

  const confirmationDevControls =
    step === "confirmation" ? (
      <div className="fe2-preview-dev" aria-label="Confirmation mock states">
        <span className="fe2-preview-dev__label">Confirmation states</span>
        {CONFIRMATION_PREVIEW_STATES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={
              confirmationStateId === s.id
                ? "fe2-preview-dev__btn fe2-preview-dev__btn--active"
                : "fe2-preview-dev__btn"
            }
            onClick={() => setConfirmationStateId(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
    ) : null;

  let body = null;
  if (step === "search") {
    body = (
      <FlightSearchScreen
        initialSearch={search}
        onSearch={(next) => {
          setSearch(next);
          setStep("results");
        }}
      />
    );
  } else if (step === "results") {
    body = (
      <FlightResultsScreen
        offers={MOCK_OFFERS}
        search={search}
        selectedOfferId={selectedOfferId}
        onEditSearch={() => setStep("search")}
        onSelectOffer={(offer) => {
          setSelectedOfferId(offer.id);
          const matched = DETAILS_PREVIEW_STATES.find((s) => s.offerId === offer.id);
          if (matched) setDetailsStateId(matched.id);
          setShowReprice(false);
          setStep("details");
        }}
      />
    );
  } else if (step === "details") {
    body = (
      <FlightDetailsScreen
        offer={selectedOffer}
        search={search}
        showReprice={showReprice}
        reprice={MOCK_REPRICE}
        onAcceptReprice={() => setShowReprice(false)}
        onDismissReprice={() => {
          setShowReprice(false);
          setStep("results");
        }}
        onContinue={() => {
          setSelectedOfferId(detailsOfferId);
          setStep("passengers");
        }}
        onBack={() => setStep("results")}
      />
    );
  } else if (step === "passengers") {
    body = (
      <PassengersScreen
        offer={getMockOfferById(selectedOfferId)}
        search={search}
        onContinue={() => setStep("extras")}
        onBack={() => setStep("details")}
      />
    );
  } else if (step === "extras") {
    body = (
      <ExtrasScreen
        key={extrasStateId}
        offer={getMockOfferById(selectedOfferId)}
        search={search}
        catalog={extrasCatalog}
        onContinue={() => setStep("review")}
        onBack={() => setStep("passengers")}
      />
    );
  } else if (step === "review") {
    body = (
      <ReviewScreen
        offer={getMockOfferById(selectedOfferId)}
        search={search}
        onContinue={() => setStep("payment")}
        onBack={() => setStep("extras")}
        onEditSection={(target) => {
          if (target === "details" || target === "passengers" || target === "extras") {
            setStep(target);
          }
        }}
      />
    );
  } else if (step === "payment") {
    body = (
      <PaymentScreen
        key={paymentStateId}
        offer={getMockOfferById(selectedOfferId)}
        search={search}
        initialMethodId={paymentStateId}
        onContinue={() => setStep("confirmation")}
        onBack={() => setStep("review")}
      />
    );
  } else {
    body = (
      <ConfirmationScreen
        key={confirmationStateId}
        stateId={confirmationStateId}
        onAction={(actionId) => {
          if (actionId === "pay" || actionId === "payment" || actionId === "retry") {
            setStep("payment");
            return;
          }
          if (actionId === "results") {
            setStep("results");
          }
        }}
      />
    );
  }

  return (
    <FlightEngineShell
      step={step}
      title={TITLES[step] || "Flight Engine V2"}
      subtitle="Design Preview · mock data · ללא integration"
      showProgress={showProgress}
      previewNav={
        <>
          {previewNav}
          {detailsDevControls}
          {extrasDevControls}
          {paymentDevControls}
          {confirmationDevControls}
        </>
      }
      bare={bare}
    >
      {body}
    </FlightEngineShell>
  );
}
